import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Card,
  SimpleGrid,
  Badge,
  Progress,
  Stat,
  NativeSelect,
  Button,
  Table,
  Spinner,
  Flex,
  Alert,
  Icon,
  Tabs,
  Dialog,
} from '@chakra-ui/react';
import {
  FiUsers,
  FiDollarSign,
  FiActivity,
  FiDownload,
  FiBarChart,
  FiEye,
  FiInfo,
  FiTrendingUp,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useColorModeValue } from '../hooks/useColorModeValue';
import { useRevenueAnalytics, useUsageAnalytics, useUsageOverview, useTopUsers, usePerformanceMetrics } from '../hooks/useApi';
import { toaster } from './ui/toaster';

const AnalyticsReporting: React.FC = () => {
  const { t } = useTranslation();
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const { data: revenueAnalytics, isLoading: revenueLoading, error: revenueError } = useRevenueAnalytics(selectedPeriod);
  const { data: usageAnalytics, isLoading: usageLoading, error: usageError } = useUsageAnalytics(selectedPeriod);
  const { data: usageOverview, isLoading: overviewLoading, error: overviewError } = useUsageOverview();
  const { data: topUsersData, isLoading: topUsersLoading, error: topUsersError } = useTopUsers(selectedPeriod);
  const { data: performanceMetrics, isLoading: performanceLoading, error: performanceError } = usePerformanceMetrics();
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Debug logging
  console.log('Analytics Data:', {
    revenueAnalytics,
    usageAnalytics,
    usageOverview,
    topUsersData,
    performanceMetrics,
    errors: {
      revenueError,
      usageError,
      overviewError,
      topUsersError,
      performanceError
    }
  });

  // Additional debug logging for Usage Overview card
  console.log('Usage Overview Card Debug:', {
    usageAnalyticsTotalReviews: usageAnalytics?.total_reviews,
    usageOverviewTotalReviews: usageOverview?.total_reviews_this_month,
    usageAnalyticsLoaded: !!usageAnalytics,
    usageOverviewLoaded: !!usageOverview,
    usageAnalyticsLoading: usageLoading,
    usageOverviewLoading: overviewLoading
  });

  if (revenueLoading || usageLoading || overviewLoading || topUsersLoading || performanceLoading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>{t('admin.security.analytics.loading')}</Text>
      </Box>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  const generateReport = () => {
    setIsModalOpen(true);
  };

  const downloadReport = (type: string) => {
    // Simulate report download
    toaster.create({
      title: t('admin.security.analytics.toasts.reportGenerated'),
      description: t('admin.security.analytics.toasts.reportPreparing', { type: getReportTypeLabel(type) }),
      type: 'success',
    });
  };

  const getPeriodLabel = (period: string) => {
    switch (period) {
      case '7d': return t('admin.security.analytics.periods.last7Days');
      case '30d': return t('admin.security.analytics.periods.last30Days');
      case '90d': return t('admin.security.analytics.periods.last90Days');
      case '1y': return t('admin.security.analytics.periods.lastYear');
      default: return t('admin.security.analytics.periods.last30Days');
    }
  };

  const getReportTypeLabel = (type: string) => {
    switch (type) {
      case 'revenue': return t('admin.security.analytics.reportTypes.revenue');
      case 'usage': return t('admin.security.analytics.reportTypes.usage');
      case 'users': return t('admin.security.analytics.reportTypes.users');
      case 'comprehensive': return t('admin.security.analytics.reportTypes.comprehensive');
      default: return type;
    }
  };

  // Calculate success rate based on completed reviews
  const calculateSuccessRate = () => {
    if (!usageAnalytics?.total_reviews) return 0;
    // This would need to be calculated from actual review status data
    // For now, using a placeholder calculation
    return 94.2; // This should come from backend
  };

  const successRate = calculateSuccessRate();

  return (
    <Box p={6}>
      <VStack gap={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Heading size="lg">{t('admin.security.analytics.title')}</Heading>
          <HStack gap={4}>
            <NativeSelect.Root size="sm" width="150px">
              <NativeSelect.Field
                value={selectedPeriod}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedPeriod(e.target.value)}
              >
                <option value="7d">{t('admin.security.analytics.periods.last7Days')}</option>
                <option value="30d">{t('admin.security.analytics.periods.last30Days')}</option>
                <option value="90d">{t('admin.security.analytics.periods.last90Days')}</option>
                <option value="1y">{t('admin.security.analytics.periods.lastYear')}</option>
              </NativeSelect.Field>
            </NativeSelect.Root>
            <Button
              colorScheme="blue"
              size="sm"
              onClick={() => generateReport()}
            >
              <FiDownload />
              {t('admin.security.analytics.buttons.generateReport')}
            </Button>
          </HStack>
        </Flex>

        {/* Period Summary */}
        <Alert.Root status="info" borderRadius="md">
          <Alert.Indicator>
            <Icon as={FiInfo} />
          </Alert.Indicator>
          <Box>
            <Alert.Title>{t('admin.security.analytics.periodSummary.title')}</Alert.Title>
            <Alert.Description>
              {t('admin.security.analytics.periodSummary.description', { period: getPeriodLabel(selectedPeriod), code: selectedPeriod })}
            </Alert.Description>
          </Box>
        </Alert.Root>

        {/* Key Metrics */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Body>
              <Stat.Root>
                <Stat.Label color="gray.500">{t('admin.security.analytics.metrics.totalRevenue')}</Stat.Label>
                <Stat.ValueText fontSize="2xl" color="green.500">
                  {formatCurrency(revenueAnalytics?.total_revenue || 0)}
                </Stat.ValueText>
                <Stat.HelpText>
                  <Icon as={FiTrendingUp} color="green.500" />
                  {t('admin.security.analytics.metrics.revenueThisPeriod')}
                </Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Body>
              <Stat.Root>
                <Stat.Label color="gray.500">{t('admin.security.analytics.metrics.totalReviews')}</Stat.Label>
                <Stat.ValueText fontSize="2xl" color="blue.500">
                  {formatNumber(usageAnalytics?.total_reviews || 0)}
                </Stat.ValueText>
                <Stat.HelpText>
                  <Icon as={FiTrendingUp} color="green.500" />
                  {t('admin.security.analytics.metrics.reviewsThisPeriod')}
                </Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Body>
              <Stat.Root>
                <Stat.Label color="gray.500">{t('admin.security.analytics.metrics.activeUsers')}</Stat.Label>
                <Stat.ValueText fontSize="2xl" color="purple.500">
                  {formatNumber(usageAnalytics?.active_users || 0)}
                </Stat.ValueText>
                <Stat.HelpText>
                  <Icon as={FiTrendingUp} color="green.500" />
                  {t('admin.security.analytics.metrics.usersThisPeriod')}
                </Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>

          <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
            <Card.Body>
              <Stat.Root>
                <Stat.Label color="gray.500">{t('admin.security.analytics.metrics.avgReviewsPerUser')}</Stat.Label>
                <Stat.ValueText fontSize="2xl" color="orange.500">
                  {(usageAnalytics?.average_reviews_per_user || 0).toFixed(1)}
                </Stat.ValueText>
                <Stat.HelpText>
                  <Icon as={FiTrendingUp} color="green.500" />
                  {t('admin.security.analytics.metrics.averagePerUser')}
                </Stat.HelpText>
              </Stat.Root>
            </Card.Body>
          </Card.Root>
        </SimpleGrid>

        {/* Detailed Analytics */}
        <Tabs.Root variant="enclosed" colorScheme="blue">
          <Tabs.List>
            <Tabs.Trigger value="overview">{t('admin.security.analytics.tabs.overview')}</Tabs.Trigger>
            <Tabs.Trigger value="revenue">{t('admin.security.analytics.tabs.revenue')}</Tabs.Trigger>
            <Tabs.Trigger value="usage">{t('admin.security.analytics.tabs.usage')}</Tabs.Trigger>
            <Tabs.Trigger value="performance">{t('admin.security.analytics.tabs.performance')}</Tabs.Trigger>
            <Tabs.Trigger value="topusers">{t('admin.security.analytics.tabs.topUsers')}</Tabs.Trigger>
          </Tabs.List>

          {/* Overview Tab */}
          <Tabs.Content value="overview">
            <VStack gap={6} align="stretch">
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
                <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
                  <Card.Header>
                    <Heading size="md">{t('admin.security.analytics.sections.revenueTrends')}</Heading>
                  </Card.Header>
                  <Card.Body>
                    <VStack gap={4} align="stretch">
                        <HStack justify="space-between">
                          <Text>{t('admin.security.analytics.labels.periodRevenue')}</Text>
                          <Badge colorScheme="green">
                            {formatCurrency(revenueAnalytics?.total_revenue || 0)}
                          </Badge>
                        </HStack>
                        <HStack justify="space-between">
                          <Text>{t('admin.security.analytics.labels.averagePerReview')}</Text>
                          <Badge colorScheme="blue">
                            {formatCurrency(revenueAnalytics?.average_revenue_per_review || 0)}
                          </Badge>
                        </HStack>
                        <HStack justify="space-between">
                          <Text>{t('admin.security.analytics.labels.totalReviewsCharged')}</Text>
                          <Badge colorScheme="purple">
                            {formatNumber(revenueAnalytics?.total_reviews_charged || 0)}
                          </Badge>
                        </HStack>
                        <Progress.Root 
                          value={revenueAnalytics?.total_revenue ? 100 : 0} 
                          colorScheme="green" 
                          size="sm"
                        >
                          <Progress.Track>
                            <Progress.Range />
                          </Progress.Track>
                        </Progress.Root>
                      </VStack>
                    </Card.Body>
                  </Card.Root>

                  <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
                    <Card.Header>
                      <Heading size="md">{t('admin.security.analytics.sections.usageOverview')}</Heading>
                    </Card.Header>
                    <Card.Body>
                      <VStack gap={4} align="stretch">
                        <HStack justify="space-between">
                          <Text>{t('admin.security.analytics.labels.totalReviewsThisMonth')}</Text>
                          <Badge colorScheme="blue">
                            {formatNumber(usageAnalytics?.total_reviews || 0)}
                          </Badge>
                        </HStack>
                        <HStack justify="space-between">
                          <Text>{t('admin.security.analytics.labels.overageRevenue')}</Text>
                          <Badge colorScheme="orange">
                            {formatCurrency(usageAnalytics?.total_overage_revenue || 0)}
                          </Badge>
                        </HStack>
                        <HStack justify="space-between">
                          <Text>{t('admin.security.analytics.labels.successRate')}</Text>
                          <Badge colorScheme="green">{successRate}%</Badge>
                        </HStack>
                        <Progress.Root value={successRate} colorScheme="green" size="sm">
                          <Progress.Track>
                            <Progress.Range />
                          </Progress.Track>
                        </Progress.Root>
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </SimpleGrid>

                <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
                  <Card.Header>
                    <Heading size="md">{t('admin.security.analytics.sections.quickActions')}</Heading>
                  </Card.Header>
                  <Card.Body>
                    <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
                      <Button
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => downloadReport('revenue')}
                      >
                        <FiDownload />
                        {t('admin.security.analytics.reportTypes.revenue')}
                      </Button>
                      <Button
                        colorScheme="green"
                        variant="outline"
                        onClick={() => downloadReport('usage')}
                      >
                        <FiDownload />
                        {t('admin.security.analytics.reportTypes.usage')}
                      </Button>
                      <Button
                        colorScheme="purple"
                        variant="outline"
                        onClick={() => downloadReport('comprehensive')}
                      >
                        <FiDownload />
                        {t('admin.security.analytics.reportTypes.comprehensive')}
                      </Button>
                    </SimpleGrid>
                  </Card.Body>
                </Card.Root>
              </VStack>
            </Tabs.Content>

            {/* Revenue Analytics Tab */}
            <Tabs.Content value="revenue">
              <VStack gap={6} align="stretch">
                <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
                  <Card.Header>
                    <Heading size="md">{t('admin.security.analytics.sections.revenueBreakdown')}</Heading>
                  </Card.Header>
                  <Card.Body>
                      <Table.Root variant="line" size="sm">
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeader>{t('admin.security.analytics.table.date')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('admin.security.analytics.table.revenue')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('admin.security.analytics.table.reviews')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('admin.security.analytics.table.avgPerReview')}</Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {revenueAnalytics?.daily_revenue && 
                            Object.entries(revenueAnalytics.daily_revenue).map(([date, revenue]) => (
                              <Table.Row key={date}>
                                <Table.Cell>{new Date(date).toLocaleDateString()}</Table.Cell>
                                <Table.Cell>{formatCurrency(revenue as number)}</Table.Cell>
                                <Table.Cell>{Math.floor((revenue as number) / (revenueAnalytics.average_revenue_per_review || 1))}</Table.Cell>
                                <Table.Cell>{formatCurrency(revenueAnalytics.average_revenue_per_review || 0)}</Table.Cell>
                              </Table.Row>
                            ))
                          }
                          {(!revenueAnalytics?.daily_revenue || Object.keys(revenueAnalytics.daily_revenue).length === 0) && (
                            <Table.Row>
                              <Table.Cell colSpan={4} textAlign="center" color="gray.500">
                                {t('admin.security.analytics.empty.noRevenueData')}
                              </Table.Cell>
                            </Table.Row>
                          )}
                        </Table.Body>
                      </Table.Root>
                  </Card.Body>
                </Card.Root>
              </VStack>
            </Tabs.Content>

            {/* Usage Analytics Tab */}
            <Tabs.Content value="usage">
              <VStack gap={6} align="stretch">
                <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
                  <Card.Header>
                    <Heading size="md">{t('admin.security.analytics.sections.usageBreakdown')}</Heading>
                  </Card.Header>
                  <Card.Body>
                      <Table.Root variant="line" size="sm">
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeader>{t('admin.security.analytics.table.month')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('admin.security.analytics.table.reviews')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('admin.security.analytics.labels.overageRevenue')}</Table.ColumnHeader>
                            <Table.ColumnHeader>{t('admin.security.analytics.metrics.activeUsers')}</Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {usageAnalytics?.monthly_usage && 
                            Object.entries(usageAnalytics.monthly_usage).map(([month, data]) => {
                              const usageData = data as { reviews: number; overage: number };
                              return (
                                <Table.Row key={month}>
                                  <Table.Cell>{month}</Table.Cell>
                                  <Table.Cell>{formatNumber(usageData.reviews)}</Table.Cell>
                                  <Table.Cell>{formatCurrency(usageData.overage)}</Table.Cell>
                                  <Table.Cell>{Math.floor(usageData.reviews / (usageAnalytics.average_reviews_per_user || 1))}</Table.Cell>
                                </Table.Row>
                              );
                            })
                          }
                          {(!usageAnalytics?.monthly_usage || Object.keys(usageAnalytics.monthly_usage).length === 0) && (
                            <Table.Row>
                              <Table.Cell colSpan={4} textAlign="center" color="gray.500">
                                {t('admin.security.analytics.empty.noUsageData')}
                              </Table.Cell>
                            </Table.Row>
                          )}
                        </Table.Body>
                      </Table.Root>
                  </Card.Body>
                </Card.Root>
              </VStack>
            </Tabs.Content>

            {/* Performance Tab */}
            <Tabs.Content value="performance">
              <VStack gap={6} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
                  <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
                    <Card.Body>
                      <Stat.Root>
                        <Stat.Label color="gray.500">{t('admin.security.analytics.performance.apiResponseTime')}</Stat.Label>
                        <Stat.ValueText fontSize="xl" color="green.500">
                          {performanceMetrics?.api_response_time || 0}ms
                        </Stat.ValueText>
                        <Stat.HelpText>
                          {t('admin.security.analytics.performance.averageResponseTime')}
                        </Stat.HelpText>
                      </Stat.Root>
                    </Card.Body>
                  </Card.Root>

                  <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
                    <Card.Body>
                      <Stat.Root>
                        <Stat.Label color="gray.500">{t('admin.security.analytics.performance.systemUptime')}</Stat.Label>
                        <Stat.ValueText fontSize="xl" color="blue.500">
                          {performanceMetrics?.system_uptime || 0}%
                        </Stat.ValueText>
                        <Stat.HelpText>
                          {t('admin.security.analytics.performance.systemAvailability')}
                        </Stat.HelpText>
                      </Stat.Root>
                    </Card.Body>
                  </Card.Root>

                  <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
                    <Card.Body>
                      <Stat.Root>
                        <Stat.Label color="gray.500">{t('admin.security.analytics.performance.activeSessions')}</Stat.Label>
                        <Stat.ValueText fontSize="xl" color="purple.500">
                          {formatNumber(performanceMetrics?.active_sessions || 0)}
                        </Stat.ValueText>
                        <Stat.HelpText>
                          {t('admin.security.analytics.performance.currentActiveUsers')}
                        </Stat.HelpText>
                      </Stat.Root>
                    </Card.Body>
                  </Card.Root>

                  <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
                    <Card.Body>
                      <Stat.Root>
                        <Stat.Label color="gray.500">{t('admin.security.analytics.performance.dataProcessed')}</Stat.Label>
                        <Stat.ValueText fontSize="xl" color="orange.500">
                          {performanceMetrics?.data_processed_gb || 0}GB
                        </Stat.ValueText>
                        <Stat.HelpText>
                          {t('admin.security.analytics.performance.totalDataProcessed')}
                        </Stat.HelpText>
                      </Stat.Root>
                    </Card.Body>
                  </Card.Root>
                </SimpleGrid>

                <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
                  <Card.Header>
                    <Heading size="md">{t('admin.security.analytics.sections.systemHealth')}</Heading>
                  </Card.Header>
                  <Card.Body>
                    <VStack gap={4} align="stretch">
                      <HStack justify="space-between">
                        <Text>{t('admin.security.analytics.performance.cpuUsage')}</Text>
                        <Badge colorScheme={performanceMetrics?.cpu_usage > 80 ? "red" : performanceMetrics?.cpu_usage > 60 ? "yellow" : "green"}>
                          {performanceMetrics?.cpu_usage || 0}%
                        </Badge>
                      </HStack>
                      <HStack justify="space-between">
                        <Text>{t('admin.security.analytics.performance.memoryUsage')}</Text>
                        <Badge colorScheme={performanceMetrics?.memory_usage > 80 ? "red" : performanceMetrics?.memory_usage > 60 ? "yellow" : "green"}>
                          {performanceMetrics?.memory_usage || 0}%
                        </Badge>
                      </HStack>
                      <HStack justify="space-between">
                        <Text>{t('admin.security.analytics.performance.diskUsage')}</Text>
                        <Badge colorScheme={performanceMetrics?.disk_usage > 80 ? "red" : performanceMetrics?.disk_usage > 60 ? "yellow" : "green"}>
                          {performanceMetrics?.disk_usage || 0}%
                        </Badge>
                      </HStack>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              </VStack>
            </Tabs.Content>

            {/* Top Users Tab */}
            <Tabs.Content value="topusers">
              <Card.Root bg={cardBg} border="1px" borderColor={borderColor}>
                <Card.Header>
                  <Heading size="md">{t('admin.security.analytics.sections.topUsersByActivity')}</Heading>
                </Card.Header>
                <Card.Body>
                    <Table.Root variant="line" size="sm">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>{t('admin.security.analytics.table.user')}</Table.ColumnHeader>
                          <Table.ColumnHeader>{t('admin.security.analytics.table.email')}</Table.ColumnHeader>
                          <Table.ColumnHeader>{t('admin.security.analytics.table.reviews')}</Table.ColumnHeader>
                          <Table.ColumnHeader>{t('admin.security.analytics.table.revenueGenerated')}</Table.ColumnHeader>
                          <Table.ColumnHeader>{t('admin.security.analytics.table.actions')}</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {topUsersData?.top_users && topUsersData.top_users.length > 0 ? (
                          topUsersData.top_users.map((user: any, index: number) => (
                            <Table.Row key={index}>
                              <Table.Cell fontWeight="medium">{user.name}</Table.Cell>
                              <Table.Cell>{user.email}</Table.Cell>
                              <Table.Cell>
                                <Badge colorScheme="blue">{user.reviews}</Badge>
                              </Table.Cell>
                              <Table.Cell>{formatCurrency(user.revenue)}</Table.Cell>
                              <Table.Cell>
                                <Button size="xs" variant="ghost">
                                  <FiEye /> {t('admin.security.analytics.buttons.viewDetails')}
                                </Button>
                              </Table.Cell>
                            </Table.Row>
                          ))
                        ) : (
                          <Table.Row>
                            <Table.Cell colSpan={5} textAlign="center" color="gray.500">
                              {t('admin.security.analytics.empty.noUserActivity')}
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </Table.Body>
                    </Table.Root>
                </Card.Body>
              </Card.Root>
            </Tabs.Content>
          </Tabs.Root>

        {/* Report Generation Modal */}
        <Dialog.Root open={isModalOpen} onOpenChange={(e) => setIsModalOpen(e.open)} size="lg">
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>{t('admin.security.analytics.buttons.generateReport')}</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={6}>
                <VStack gap={4} align="stretch">
                  <Text>{t('admin.security.analytics.dialog.selectReportType')}</Text>
                  
                  <SimpleGrid columns={1} gap={3}>
                    <Button
                      variant="outline"
                      onClick={() => downloadReport('revenue')}
                      justifyContent="flex-start"
                    >
                      <FiDollarSign /> {t('admin.security.analytics.reportTypes.revenue')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => downloadReport('usage')}
                      justifyContent="flex-start"
                    >
                      <FiActivity /> {t('admin.security.analytics.reportTypes.usage')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => downloadReport('users')}
                      justifyContent="flex-start"
                    >
                      <FiUsers /> {t('admin.security.analytics.reportTypes.userActivity')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => downloadReport('comprehensive')}
                      justifyContent="flex-start"
                    >
                      <FiBarChart /> {t('admin.security.analytics.reportTypes.comprehensiveAnalytics')}
                    </Button>
                  </SimpleGrid>
                </VStack>
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      </VStack>
    </Box>
  );
};

export default AnalyticsReporting; 